import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { TFilters } from 'redux/types';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgOutboxMessage } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields, PgJsonView } from '../common/PgFields';
import { PgList } from '../common/PgList';
import { PgStatusChip } from '../common/PgMiniTable';
import { useReloadKey } from '../common/helpers';
import { DETAILS_BUTTON, pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { usePaymentViewer } from '../Payments/PaymentViewer';
import { formatDateTime } from '../utils';

type TRow = TPgOutboxMessage;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const RETRY = 'Retry';
const RETRY_BUTTON = { name: RETRY, label: 'Retry', icon: EIconButtons.HISTORY };

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Created', 'createdAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Message', 'messageType', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Handler', 'handler', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(['pending', 'processing', 'done', 'dead']),
	}),
	col('Park reason', 'parkReason', ETableStructureType.STRING, { sortable: true }),
	col('Attempts', 'attempts', ETableStructureType.STRING, {
		sortable: true,
		value: (item) => String(item.attempts),
	}),
	col('Next attempt', 'nextAttemptAt', ETableStructureType.DATE_TIME, { sortable: true }),
	col('Ordering key', 'orderingKey', ETableStructureType.STRING, {
		sortable: true,
		filterable: true,
	}),
	col('Last error', 'lastError', ETableStructureType.STRING, {
		tooltip: (item) => item.lastError ?? '',
	}),
	pgActionsColumn<TRow>([DETAILS_BUTTON], (item) =>
		item.status === 'dead' ? [DETAILS_BUTTON, RETRY_BUTTON] : [DETAILS_BUTTON],
	),
];

/**
 * Outbox of the gateway (03 §4.12): `dead` messages and messages parked for a reason (`park_reason`)
 * need an operator. Retry moves a dead message back to `pending`; `resetProgress` also clears the
 * steps already done (after an EAS conflict was resolved by hand).
 */
export const OutboxList: FC = () => {
	const { openPayment } = usePaymentViewer();
	const [onlyDead, setOnlyDead] = useState(false);
	const [detailsId, setDetailsId] = useState<number | null>(null);
	const [detail, setDetail] = useState<TPgOutboxMessage | null>(null);
	const [detailError, setDetailError] = useState<string | null>(null);
	const [retryRow, setRetryRow] = useState<TRow | null>(null);
	const [resetProgress, setResetProgress] = useState(false);
	const [retryBusy, setRetryBusy] = useState(false);
	const [retryError, setRetryError] = useState<string | null>(null);
	const [reloadKey, reload] = useReloadKey();

	const extraFilters = useMemo<TFilters>(
		() => (onlyDead ? [{ fieldName: 'status', name: 'status', value: 'dead', operator: '=' }] : []),
		[onlyDead],
	);

	useEffect(() => {
		setDetail(null);
		setDetailError(null);
		if (detailsId === null) return;
		api.paymentGateway
			.getOutboxMessage(detailsId)
			.then(setDetail)
			.catch((e) => setDetailError(getPgErrorMessage(e, 'Failed to load the message')));
	}, [detailsId]);

	const handleAction = useCallback((row: TRow, action: string) => {
		if (action === DETAILS_BUTTON.name) setDetailsId(row.id);
		if (action === RETRY) {
			setResetProgress(false);
			setRetryError(null);
			setRetryRow(row);
		}
	}, []);

	const retry = async () => {
		if (!retryRow) return;
		setRetryBusy(true);
		setRetryError(null);
		try {
			await api.paymentGateway.retryOutboxMessage({ id: retryRow.id, body: { resetProgress } });
			notificationContainer('The message is queued again', 'success');
			setRetryRow(null);
			reload();
		} catch (e) {
			setRetryError(getPgErrorMessage(e, 'Retry failed'));
		} finally {
			setRetryBusy(false);
		}
	};

	return (
		<>
			<PgList<TRow>
				title="Outbox"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getOutboxMessages}
				onAction={handleAction}
				extraFilters={extraFilters}
				defaultOrder={{ fieldName: 'createdAt', direction: ESortDirection.DESC }}
				reloadKey={reloadKey}
				errorText="Failed to load outbox messages"
				headerContent={
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={onlyDead}
								onChange={(e) => setOnlyDead(e.target.checked)}
							/>
						}
						label="Dead only"
					/>
				}
			/>
			<Dialog open={detailsId !== null} onClose={() => setDetailsId(null)} maxWidth="md" fullWidth>
				<DialogTitle color="primary">Outbox message</DialogTitle>
				<DialogContent dividers>
					{!detail && !detailError && <CircularProgress size={24} />}
					{detailError && <Alert severity="error">{detailError}</Alert>}
					{detail && (
						<>
							<PgFields
								items={[
									['Message', detail.messageType],
									['Handler', detail.handler],
									['Status', <PgStatusChip key="s" value={detail.status} />],
									['Park reason', detail.parkReason],
									['Attempts', detail.attempts],
									['Ordering key', detail.orderingKey],
									['Correlation ID', detail.correlationId],
									['Created', formatDateTime(detail.createdAt)],
									['Next attempt', formatDateTime(detail.nextAttemptAt)],
									['Processed', formatDateTime(detail.processedAt)],
									['Locked by', detail.lockedBy],
									['Locked until', formatDateTime(detail.lockedUntil)],
									['Last error', detail.lastError],
								]}
							/>
							{detail.correlationId && GUID_REGEX.test(detail.correlationId) && (
								<Button
									size="small"
									sx={{ mt: 1 }}
									onClick={() => detail.correlationId && openPayment(detail.correlationId)}
								>
									Open the payment
								</Button>
							)}
							<Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 0.5 }}>
								Payload
							</Typography>
							<PgJsonView value={detail.payload} maxHeight={240} />
							<Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 0.5 }}>
								Progress (steps already done)
							</Typography>
							<PgJsonView value={detail.progress} maxHeight={200} />
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDetailsId(null)}>Close</Button>
				</DialogActions>
			</Dialog>
			<PgConfirmDialog
				open={!!retryRow}
				title="Retry the outbox message"
				confirmLabel="Retry"
				confirmColor="warning"
				busy={retryBusy}
				error={retryError}
				onConfirm={() => void retry()}
				onClose={() => setRetryRow(null)}
				description={`The dead message ${retryRow?.messageType ?? ''} for ${retryRow?.handler ?? ''} becomes "pending" and is processed again.`}
			>
				<FormControlLabel
					control={
						<Checkbox
							checked={resetProgress}
							onChange={(e) => setResetProgress(e.target.checked)}
							disabled={retryBusy}
						/>
					}
					label="Clear the progress (only after an EAS conflict was resolved by hand: the steps already done are repeated)"
				/>
			</PgConfirmDialog>
		</>
	);
};
